import { type KeyboardEvent, type RefObject, useEffect, useRef } from 'react';
import { useDismiss } from '../builder/use-dismiss';
import type { PopupAnchor } from './anchored';
import { Anchored } from './anchored';

export interface DrillMenuItem {
  key: string;
  label: string;
  /** The full name, when `label` is the owner's shortening of it. */
  title?: string;
  /** One line under the label — what it groups by, and what does not carry. */
  detail: string;
  /** A short word beside the label. The model drill says it ties out. */
  badge?: string;
  onSelect: () => void;
}

export interface DrillMenuProps {
  /** "Drill into", or "Drill deeper" once inside a drill. */
  heading: string;
  /** The coordinate, in words — what the reader pointed at. */
  coordinate: string;
  /** The rows behind the figure. Its own group, first, when offered at all. */
  entries?: DrillMenuItem;
  /** Saved reports this click may land on, already ranked. */
  targets: readonly DrillMenuItem[];
  /**
   * A line in place of the saved reports while they cannot be listed — still
   * loading, or failed. Without it an empty group reads as "there are none".
   */
  targetsNote?: string;
  anchor: PopupAnchor;
  onClose: () => void;
  /** The cell that opened it, so pressing it again does not reopen at once. */
  toggleRef?: RefObject<HTMLElement | null>;
}

/**
 * Where a clicked figure may go.
 *
 * TWO KINDS OF DESTINATION, labelled apart. The entries are automatic — every
 * figure has them, nobody configured anything, and they always add back up to
 * the number clicked. A saved report under them is a LAYOUT the reader may
 * prefer — its columns, its order, its sort — asked the question the cell
 * asks; its own filters and period do not come with it, and the line under it
 * says so before it is picked rather than after.
 */
export function DrillMenu({
  heading,
  coordinate,
  entries,
  targets,
  targetsNote,
  anchor,
  onClose,
  toggleRef,
}: DrillMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(true, ref, onClose, toggleRef);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('.rk-drillmenu-item')?.focus();
  }, []);

  // Arrows walk the destinations; Tab reaches them too.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('.rk-drillmenu-item') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'ArrowDown' ? at + 1 : at - 1;
    items[(next + items.length) % items.length]?.focus();
    event.preventDefault();
  };

  const offered = (entries ? 1 : 0) + targets.length;
  return (
    <div
      ref={ref}
      // `rk-scope` carries the theme tokens. The menu is an overlay the host
      // mounts beside the builder, not inside it, and without the class every
      // colour here resolves to nothing — a transparent menu over the report.
      className="rk-scope rk-drillmenu"
      role="dialog"
      aria-label={`${heading}: ${coordinate}`}
      onKeyDown={onKeyDown}
    >
      <Anchored panelRef={ref} anchor={anchor} />
      <div className="rk-drillmenu-head">
        <span className="rk-drillmenu-eyebrow">{heading}</span>
        <span className="rk-drillmenu-coord">{coordinate}</span>
      </div>

      {entries && (
        <div className="rk-drillmenu-group">
          <Item item={entries} primary />
        </div>
      )}

      {targets.length > 0 && (
        <div className="rk-drillmenu-group rk-drillmenu-targets">
          <div className="rk-drillmenu-grouplabel">
            Saved reports — their layout, this cell’s filters and period
          </div>
          {targets.map((item) => (
            <Item key={item.key} item={item} />
          ))}
        </div>
      )}

      {targetsNote && <p className="rk-drillmenu-empty">{targetsNote}</p>}

      {offered === 0 && !targetsNote && (
        <p className="rk-drillmenu-empty">Nothing to drill into from here.</p>
      )}
    </div>
  );
}

function Item({ item, primary }: { item: DrillMenuItem; primary?: boolean }) {
  return (
    <button
      type="button"
      className={`rk-drillmenu-item${primary ? ' rk-drillmenu-primary' : ''}`}
      title={item.title}
      onClick={item.onSelect}
    >
      <span className="rk-drillmenu-label">
        {item.label}
        {item.badge && <span className="rk-drillmenu-badge">{item.badge}</span>}
      </span>
      <span className="rk-drillmenu-detail">{item.detail}</span>
    </button>
  );
}
